import React, { useEffect, useId, useRef } from "react";
import { Loader2, Pause, Play, VolumeX } from "lucide-react";
import { rgba } from "../../lib/kitColor";

// The catalogue's play/pause control, built like the plugin's knobs (the
// Sample Editor's rotary LookAndFeel): a flat matte face (#2E2E32) sitting on
// three stacked black shadows, a thin white edge ring, and a white glyph with
// a faint halo. The knob's accent arc becomes the playback ring: while the
// row plays it fills clockwise from 12 o'clock with the loop's position, in
// the row's own waveform colours, over a dim track, just as the plugin draws
// an accent arc at 14% under its 96% stroke.

const FACE = 40; // px, the knob itself
const BOX = 52; // px, knob plus the ring around it
const RING_R = 23;
const RING_W = 3;
const CIRC = 2 * Math.PI * RING_R;

const KNOB_SHADOW = [
  "0 5px 9px 3px rgba(0, 0, 0, 0.25)", // soft outer
  "0 3.5px 4px 1px rgba(0, 0, 0, 0.4)", // mid
  "0 2px 1px 0 rgba(0, 0, 0, 0.5)", // tight
  "inset 0 0 0 0.6px rgba(255, 255, 255, 0.12)", // edge ring
].join(", ");

// The plugin's glyph treatment: the icon at 96% white over a 14% white halo.
const GLYPH_GLOW = "drop-shadow(0 0 1.5px rgba(255, 255, 255, 0.35))";

export default function PreviewKnob({ state, color, label, title, onClick, getPosition, getDuration }) {
  const isPlaying = state === "playing";
  const isLoading = state === "loading";
  const isUnavailable = state === "unavailable";
  const arcRef = useRef(null);
  const gradientId = `knob-ring-${useId().replace(/:/g, "")}`;

  // Drive the ring straight from the audio clock, one frame at a time, only
  // while this row plays; no React state, so no re-render per frame.
  useEffect(() => {
    const arc = arcRef.current;
    if (!arc) return undefined;
    if (!isPlaying) {
      arc.style.strokeDashoffset = String(CIRC);
      return undefined;
    }
    let raf = 0;
    const tick = () => {
      const d = getDuration?.() || 0;
      const p = d > 0 ? Math.min(1, Math.max(0, (getPosition?.() || 0) / d)) : 0;
      arc.style.strokeDashoffset = String(CIRC * (1 - p));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isPlaying, getPosition, getDuration]);

  const glyphOpacity = isUnavailable ? 0.38 : 0.96;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isLoading || isUnavailable}
      aria-label={label}
      title={title}
      className="group relative flex shrink-0 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 disabled:cursor-default"
      style={{ width: BOX, height: BOX }}
    >
      <svg
        className="pointer-events-none absolute inset-0 -rotate-90"
        width={BOX}
        height={BOX}
        viewBox={`0 0 ${BOX} ${BOX}`}
        aria-hidden="true"
        style={{ opacity: isPlaying ? 1 : 0, transition: "opacity 150ms" }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor={rgba(color.from, 1)} />
            <stop offset="1" stopColor={rgba(color.to, 1)} />
          </linearGradient>
        </defs>
        <circle cx={BOX / 2} cy={BOX / 2} r={RING_R} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={RING_W} />
        <circle
          ref={arcRef}
          cx={BOX / 2}
          cy={BOX / 2}
          r={RING_R}
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth={RING_W}
          strokeLinecap="round"
          strokeDasharray={CIRC}
          strokeDashoffset={CIRC}
          style={{ filter: `drop-shadow(0 0 3px ${rgba(color.to, 0.45)})` }}
        />
      </svg>

      <span
        className={`flex items-center justify-center rounded-full bg-[#2E2E32] transition-[filter] ${
          isUnavailable || isLoading ? "" : "group-hover:brightness-125 group-active:brightness-90"
        }`}
        style={{ width: FACE, height: FACE, boxShadow: KNOB_SHADOW }}
      >
        <span className="text-white" style={{ opacity: glyphOpacity, filter: isUnavailable ? "none" : GLYPH_GLOW }}>
          {isUnavailable ? (
            <VolumeX size={15} strokeWidth={1.75} />
          ) : isLoading ? (
            <Loader2 size={16} strokeWidth={1.75} className="animate-spin" />
          ) : isPlaying ? (
            <Pause size={14} fill="currentColor" strokeWidth={0} />
          ) : (
            <Play size={14} fill="currentColor" strokeWidth={0} className="ml-0.5" />
          )}
        </span>
      </span>
    </button>
  );
}
